import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mountains, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Detectar redirección por sesión expirada
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("expired") === "1") {
      setInfo("Tu sesion expiro por inactividad. Vuelve a iniciar sesion para continuar.");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setInfo(""); setLoading(true);
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password;
    try {
      await login(cleanEmail, cleanPassword);
      // Volver a la ruta original si existe
      let redirectTo = "/admin";
      try {
        const saved = sessionStorage.getItem("post_login_redirect");
        if (saved && saved.startsWith("/") && !saved.startsWith("/login")) {
          redirectTo = saved;
          sessionStorage.removeItem("post_login_redirect");
        }
      } catch { /* ignore */ }
      navigate(redirectTo);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        setError("Email o contraseña incorrectos. Revisa mayusculas/minusculas y que no haya espacios.");
      } else if (!err.response) {
        setError("No se pudo conectar al servidor. Revisa tu conexion a internet.");
      } else {
        setError(typeof detail === "string" ? detail : (t("login_error") || "Error al iniciar sesion"));
      }
    } finally { setLoading(false); }
  };

  return (
    <main className="pt-20 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm" data-testid="login-page">
        <div className="text-center mb-8">
          <Mountains size={40} weight="bold" className="text-[#FF4D00] mx-auto mb-4" />
          <h1 className="text-2xl font-black uppercase font-['Cabinet_Grotesk']">{t("admin_panel")}</h1>
          <p className="text-sm text-gray-400 mt-2">{t("login_desc")}</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/5 p-8 space-y-4" autoComplete="on">
          {info && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm p-3" data-testid="login-info">
              {info}
            </div>
          )}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3" data-testid="login-error">
              {error}
            </div>
          )}
          <div>
            <Label className="text-gray-400 text-xs">{t("email")}</Label>
            <Input
              type="email"
              autoComplete="email"
              className="bg-[#1A1A1A] border-white/10 mt-1"
              value={email}
              onChange={e => setEmail(e.target.value)}
              data-testid="login-email-input"
              required
            />
          </div>
          <div>
            <Label className="text-gray-400 text-xs">{t("password_label")}</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="bg-[#1A1A1A] border-white/10 pr-10"
                value={password}
                onChange={e => setPassword(e.target.value)}
                data-testid="login-password-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-[#FF4D00] transition-colors"
                aria-label={showPassword ? "Ocultar contrasena" : "Mostrar contrasena"}
                tabIndex={-1}
                data-testid="login-toggle-password"
              >
                {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
            {showPassword && password && (
              <p className="text-xs text-yellow-400/80 mt-1" data-testid="login-password-visible-warning">
                Contrasena visible. Ocultala antes de soltar el cursor.
              </p>
            )}
          </div>
          <Button
            type="submit"
            className="w-full bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-11"
            disabled={loading}
            data-testid="login-submit-btn"
          >
            {loading ? t("logging_in") : t("login_btn")}
          </Button>
        </form>
      </div>
    </main>
  );
}
